import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { EyeIcon, MagnifyingGlassIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  dangerButtonClass,
  DetailCard,
  DetailItem,
  Field,
  FormActions,
  FormCard,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  isQuantity,
  primaryButtonClass,
  QUANTITY_STEP,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import {
  useAllocateVendorPayment,
  useBillableLines,
  useCancelVendorBill,
  useCancelVendorPayment,
  useCreateVendorBill,
  useCreateVendorPayment,
  usePayablesAgeing,
  useSetItcStatus,
  useVendorBill,
  useVendorBills,
  useVendorBillSummary,
  useVendorPayment,
  useVendorPayments,
  useVendorPaymentSummary,
} from '../../hooks/usePayables';
import { useActiveVendors } from '../../hooks/usePurchase';
import { ItcStatus, MatchStatus, SupplyType, VendorBillInput, VendorPaymentInput } from '../../services/payables.service';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const round2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const formatDate = (value?: string | null) =>
  value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const MATCH: Record<MatchStatus, { label: string; className: string; hint: string }> = {
  matched: { label: 'Matched', className: 'bg-green-50 text-green-700 ring-green-600/20', hint: 'Against goods receipts, at the ordered price' },
  price_variance: { label: 'Price differs', className: 'bg-amber-50 text-amber-800 ring-amber-600/20', hint: 'Against goods receipts, but a price differs from the purchase order' },
  unmatched: { label: 'Not matched', className: 'bg-gray-50 text-gray-700 ring-gray-500/20', hint: 'One or more lines are not against a goods receipt' },
};
const ITC: Record<ItcStatus, string> = { undecided: 'Undecided', eligible: 'Credit taken', ineligible: 'Credit not taken' };
const BILL_STATUS: Record<string, { label: string; className: string }> = {
  posted: { label: 'Owed', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  paid: { label: 'Paid', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  cancelled: { label: 'Cancelled', className: 'bg-red-50 text-red-700 ring-red-600/20' },
};
const MODES = [
  { value: 'bank_transfer', label: 'Bank transfer (NEFT / RTGS / IMPS)' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'upi', label: 'UPI' },
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
];
const modeLabel = (mode: string) => MODES.find((option) => option.value === mode)?.label.split(' (')[0] ?? mode;

const Badge: React.FC<{ className: string; title?: string; children: React.ReactNode }> = ({ className, title, children }) => (
  <span className={cn(badgeClass, className)} title={title}>{children}</span>
);

// ---------------------------------------------------------------- vendor bill form

interface ReceiptLineState {
  quantity: string;
  unitPrice: string;
  taxRate: string;
}
interface OtherLine {
  key: string;
  description: string;
  hsnCode: string;
  quantity: string;
  unitPrice: string;
  taxRate: string;
}
let otherKey = 0;

const BillForm: React.FC<{ isPending: boolean; onCancel: () => void; onSubmit: (data: VendorBillInput) => void }> = ({ isPending, onCancel, onSubmit }) => {
  const { data: vendors = [] } = useActiveVendors();
  const [form, setForm] = useState({ vendorId: '', vendorInvoiceNumber: '', billDate: today(), dueDate: '', supplyType: '' as SupplyType | '', otherCharges: '', itcStatus: 'undecided' as ItcStatus, notes: '' });
  const [receiptLines, setReceiptLines] = useState<Record<string, ReceiptLineState>>({});
  const [otherLines, setOtherLines] = useState<OtherLine[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { data: billable = [], isLoading: billableLoading } = useBillableLines(form.vendorId || undefined);

  // Another vendor has other receipts
  useEffect(() => {
    setReceiptLines({});
  }, [form.vendorId]);

  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => {
      const { [field]: _cleared, ...rest } = prev;
      return rest;
    });
  };
  const lineOf = (id: string, fallbackPrice: number, fallbackTax: number): ReceiptLineState => receiptLines[id] ?? { quantity: '', unitPrice: String(fallbackPrice), taxRate: String(fallbackTax) };
  const setReceiptLine = (id: string, fallbackPrice: number, fallbackTax: number, field: keyof ReceiptLineState, value: string) =>
    setReceiptLines((prev) => ({ ...prev, [id]: { ...lineOf(id, fallbackPrice, fallbackTax), ...prev[id], [field]: value } }));
  const billEverything = () =>
    setReceiptLines(Object.fromEntries(billable.map((line) => [line.goodsReceiptItemId, { quantity: String(line.billableQuantity), unitPrice: String(line.orderedPrice), taxRate: String(line.taxRate) }])));

  const picked = billable
    .map((line) => ({ line, state: lineOf(line.goodsReceiptItemId, line.orderedPrice, line.taxRate) }))
    .filter(({ state }) => state.quantity.trim() !== '' && Number(state.quantity) !== 0);
  const taxable = round2(
    picked.reduce((sum, { state }) => sum + (Number(state.quantity) || 0) * (Number(state.unitPrice) || 0), 0) +
      otherLines.reduce((sum, line) => sum + (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0), 0),
  );
  const tax = round2(
    picked.reduce((sum, { state }) => sum + ((Number(state.quantity) || 0) * (Number(state.unitPrice) || 0) * (Number(state.taxRate) || 0)) / 100, 0) +
      otherLines.reduce((sum, line) => sum + ((Number(line.quantity) || 0) * (Number(line.unitPrice) || 0) * (Number(line.taxRate) || 0)) / 100, 0),
  );
  const total = round2(taxable + tax + (Number(form.otherCharges) || 0));

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!form.vendorId) next.vendorId = 'Choose the vendor';
    if (!form.vendorInvoiceNumber.trim()) next.vendorInvoiceNumber = 'Enter the number on the vendor invoice';
    if (!form.billDate) next.billDate = 'Enter the invoice date';
    else if (form.billDate > today()) next.billDate = 'Not in the future';
    if (form.dueDate && form.billDate && form.dueDate < form.billDate) next.dueDate = 'Not before the invoice date';

    const items: VendorBillInput['items'] = [];
    picked.forEach(({ line, state }) => {
      const quantity = Number(state.quantity);
      const unitPrice = Number(state.unitPrice);
      const taxRate = Number(state.taxRate);
      const key = `line-${line.goodsReceiptItemId}`;
      if (!isQuantity(quantity) || quantity <= 0) next[key] = 'Use a quantity with up to 4 decimals';
      else if (quantity > line.billableQuantity) next[key] = `Only ${formatNumber(line.billableQuantity)} are not billed yet`;
      else if (!Number.isFinite(unitPrice) || unitPrice < 0 || round2(unitPrice) !== unitPrice) next[key] = 'Enter a price with up to 2 decimals';
      else if (!Number.isFinite(taxRate) || taxRate < 0 || taxRate > 100) next[key] = 'Enter the GST rate, 0 to 100';
      else items.push({ goodsReceiptItemId: line.goodsReceiptItemId, quantity, unitPrice, taxRate, hsnCode: line.hsnCode || undefined });
    });
    otherLines.forEach((line) => {
      const quantity = Number(line.quantity);
      const unitPrice = Number(line.unitPrice);
      const taxRate = line.taxRate.trim() === '' ? NaN : Number(line.taxRate);
      if (line.description.trim().length < 2) next[line.key] = 'Describe the line';
      else if (!isQuantity(quantity) || quantity <= 0) next[line.key] = 'Enter a quantity above zero';
      else if (!Number.isFinite(unitPrice) || unitPrice < 0 || round2(unitPrice) !== unitPrice) next[line.key] = 'Enter a price with up to 2 decimals';
      else if (!Number.isFinite(taxRate) || taxRate < 0 || taxRate > 100) next[line.key] = 'Enter the GST rate, 0 to 100';
      else if (line.hsnCode.trim() && !/^(\d{4}|\d{6}|\d{8})$/.test(line.hsnCode.trim())) next[line.key] = 'The HSN code has 4, 6 or 8 digits';
      else items.push({ description: line.description.trim(), hsnCode: line.hsnCode.trim() || undefined, quantity, unitPrice, taxRate });
    });
    const lineErrors = Object.keys(next).some((key) => key.startsWith('line-') || key.startsWith('other-'));
    if (form.vendorId && items.length === 0 && !lineErrors) next.lines = 'Enter the quantity billed for at least one line';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    onSubmit({
      vendorId: form.vendorId,
      vendorInvoiceNumber: form.vendorInvoiceNumber.trim(),
      billDate: form.billDate,
      dueDate: form.dueDate || undefined,
      supplyType: form.supplyType || undefined,
      otherCharges: form.otherCharges.trim() === '' ? undefined : Number(form.otherCharges),
      itcStatus: form.itcStatus,
      notes: form.notes.trim() || null,
      items,
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <FormCard title="Vendor invoice" description="As printed on the invoice the vendor sent">
        <Field label="Vendor" htmlFor="vb-vendor" required error={errors.vendorId} className="sm:col-span-2">
          <select id="vb-vendor" value={form.vendorId} onChange={set('vendorId')} className={cn(inputClass, errors.vendorId && inputErrorClass)}>
            <option value="">Select…</option>
            {vendors.map((vendor) => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.name}
                {vendor.code ? ` (${vendor.code})` : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Vendor invoice no." htmlFor="vb-number" required error={errors.vendorInvoiceNumber}>
          <input id="vb-number" type="text" maxLength={50} value={form.vendorInvoiceNumber} onChange={set('vendorInvoiceNumber')} className={cn(inputClass, errors.vendorInvoiceNumber && inputErrorClass)} />
        </Field>
        <Field label="Invoice date" htmlFor="vb-date" required error={errors.billDate}>
          <input id="vb-date" type="date" max={today()} value={form.billDate} onChange={set('billDate')} className={cn(inputClass, errors.billDate && inputErrorClass)} />
        </Field>
        <Field label="Due date" htmlFor="vb-due" error={errors.dueDate} hint="Left empty, the vendor's payment terms are used">
          <input id="vb-due" type="date" min={form.billDate} value={form.dueDate} onChange={set('dueDate')} className={cn(inputClass, errors.dueDate && inputErrorClass)} />
        </Field>
        <Field label="GST charged as" htmlFor="vb-supply" hint="Automatic compares the GST numbers of the vendor and the company">
          <select id="vb-supply" value={form.supplyType} onChange={set('supplyType')} className={inputClass}>
            <option value="">Automatic</option>
            <option value="intra_state">Within the state (CGST + SGST)</option>
            <option value="inter_state">From another state (IGST)</option>
          </select>
        </Field>
        <Field label="Freight, packing, rounding (₹)" htmlFor="vb-other" hint="Charges on the invoice outside the lines; may be negative">
          <input id="vb-other" type="number" step="0.01" value={form.otherCharges} onChange={set('otherCharges')} className={cn(inputClass, 'text-right tabular-nums')} placeholder="0.00" />
        </Field>
        <Field label="Input tax credit" htmlFor="vb-itc" hint="Your decision; it can be changed later">
          <select id="vb-itc" value={form.itcStatus} onChange={set('itcStatus')} className={inputClass}>
            <option value="undecided">Undecided</option>
            <option value="eligible">Credit taken</option>
            <option value="ineligible">Credit not taken</option>
          </select>
        </Field>
      </FormCard>

      {form.vendorId && (
        <section className="rounded-lg border border-gray-200 bg-white">
          <div className="flex flex-col gap-2 rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h4 className="text-sm font-semibold text-gray-900">Goods received, not billed yet</h4>
              <p className="mt-0.5 text-xs text-gray-500">Enter what this invoice bills. The price starts at the purchase order price; a different price is flagged for review.</p>
            </div>
            {billable.length > 0 && (
              <button type="button" onClick={billEverything} className="self-start rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50 sm:self-auto">
                Bill everything received
              </button>
            )}
          </div>
          {billableLoading ? (
            <p className="px-4 py-6 text-sm text-gray-500">Loading…</p>
          ) : billable.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-500">Nothing received from this vendor is waiting to be billed. Add the invoice lines below.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th scope="col" className={thClass}>Received</th>
                    <th scope="col" className={cn(thClass, 'text-right')}>Not billed</th>
                    <th scope="col" className={cn(thClass, 'w-32 text-right')}>Bill quantity</th>
                    <th scope="col" className={cn(thClass, 'w-32 text-right')}>Price</th>
                    <th scope="col" className={cn(thClass, 'w-24 text-right')}>GST %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {billable.map((line) => {
                    const state = lineOf(line.goodsReceiptItemId, line.orderedPrice, line.taxRate);
                    const error = errors[`line-${line.goodsReceiptItemId}`];
                    const differs = state.quantity.trim() !== '' && Math.abs(Number(state.unitPrice) - line.orderedPrice) >= 0.005;
                    return (
                      <tr key={line.goodsReceiptItemId}>
                        <td className="px-4 py-2.5">
                          <p className="text-sm font-medium text-gray-900">{line.productName}</p>
                          <p className="text-xs text-gray-500">{line.grnNumber} · {formatDate(line.receiptDate)} · {line.purchaseOrderNumber}</p>
                          {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
                          {!error && differs && <p className="mt-1 text-xs text-amber-700">Ordered at {formatMoney(line.orderedPrice)}</p>}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-700 tabular-nums">{formatNumber(line.billableQuantity)} {line.unit}</td>
                        <td className="px-4 py-2.5">
                          <input type="number" min="0" step={QUANTITY_STEP} value={state.quantity} onChange={(event) => setReceiptLine(line.goodsReceiptItemId, line.orderedPrice, line.taxRate, 'quantity', event.target.value)} aria-label={`Bill quantity of ${line.productName}`} className={cn(inputClass, 'text-right tabular-nums', error && inputErrorClass)} />
                        </td>
                        <td className="px-4 py-2.5">
                          <input type="number" min="0" step="0.01" value={state.unitPrice} onChange={(event) => setReceiptLine(line.goodsReceiptItemId, line.orderedPrice, line.taxRate, 'unitPrice', event.target.value)} aria-label={`Price of ${line.productName}`} className={cn(inputClass, 'text-right tabular-nums')} />
                        </td>
                        <td className="px-4 py-2.5">
                          <input type="number" min="0" max="100" step="0.01" value={state.taxRate} onChange={(event) => setReceiptLine(line.goodsReceiptItemId, line.orderedPrice, line.taxRate, 'taxRate', event.target.value)} aria-label={`GST rate of ${line.productName}`} className={cn(inputClass, 'text-right tabular-nums')} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="space-y-3 border-t border-gray-200 p-4">
            {otherLines.map((line) => (
              <div key={line.key} className="rounded-md border border-gray-200 p-3">
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-12">
                  <Field label="Other line" htmlFor={`${line.key}-d`} className="col-span-2 lg:col-span-4">
                    <input id={`${line.key}-d`} type="text" maxLength={255} value={line.description} onChange={(event) => setOtherLines((prev) => prev.map((item) => (item.key === line.key ? { ...item, description: event.target.value } : item)))} className={inputClass} placeholder="For example transport charge" />
                  </Field>
                  <Field label="HSN / SAC" htmlFor={`${line.key}-h`} className="lg:col-span-2">
                    <input id={`${line.key}-h`} type="text" maxLength={8} value={line.hsnCode} onChange={(event) => setOtherLines((prev) => prev.map((item) => (item.key === line.key ? { ...item, hsnCode: event.target.value } : item)))} className={inputClass} />
                  </Field>
                  <Field label="Quantity" htmlFor={`${line.key}-q`} className="lg:col-span-2">
                    <input id={`${line.key}-q`} type="number" min="0" step={QUANTITY_STEP} value={line.quantity} onChange={(event) => setOtherLines((prev) => prev.map((item) => (item.key === line.key ? { ...item, quantity: event.target.value } : item)))} className={cn(inputClass, 'text-right tabular-nums')} />
                  </Field>
                  <Field label="Price" htmlFor={`${line.key}-p`} className="lg:col-span-2">
                    <input id={`${line.key}-p`} type="number" min="0" step="0.01" value={line.unitPrice} onChange={(event) => setOtherLines((prev) => prev.map((item) => (item.key === line.key ? { ...item, unitPrice: event.target.value } : item)))} className={cn(inputClass, 'text-right tabular-nums')} />
                  </Field>
                  <Field label="GST %" htmlFor={`${line.key}-t`} className="lg:col-span-2">
                    <input id={`${line.key}-t`} type="number" min="0" max="100" step="0.01" value={line.taxRate} onChange={(event) => setOtherLines((prev) => prev.map((item) => (item.key === line.key ? { ...item, taxRate: event.target.value } : item)))} className={cn(inputClass, 'text-right tabular-nums')} />
                  </Field>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-sm text-red-600">{errors[line.key]}</p>
                  <button type="button" onClick={() => setOtherLines((prev) => prev.filter((item) => item.key !== line.key))} className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium text-gray-500 hover:bg-red-50 hover:text-red-600">
                    <XMarkIcon className="mr-1 h-3.5 w-3.5" />
                    Remove
                  </button>
                </div>
              </div>
            ))}
            <button type="button" onClick={() => setOtherLines((prev) => [...prev, { key: `other-${otherKey++}`, description: '', hsnCode: '', quantity: '1', unitPrice: '', taxRate: '18' }])} className={secondaryButtonClass}>
              <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
              Add a line that is not against a receipt
            </button>
            {errors.lines && <p className="text-sm text-red-600">{errors.lines}</p>}
          </div>

          <dl className="grid grid-cols-3 gap-3 rounded-b-lg border-t border-gray-200 bg-gray-50 p-4 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-500">Taxable value</dt>
              <dd className="mt-1 font-semibold text-gray-900 tabular-nums">{formatMoney(taxable)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-500">GST</dt>
              <dd className="mt-1 font-semibold text-gray-900 tabular-nums">{formatMoney(tax)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-500">Bill total</dt>
              <dd className="mt-1 font-semibold text-gray-900 tabular-nums">{formatMoney(total)}</dd>
            </div>
          </dl>
        </section>
      )}

      <FormActions>
        <button type="button" onClick={onCancel} disabled={isPending} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>
          {isPending ? 'Saving…' : 'Save Bill'}
        </button>
      </FormActions>
    </form>
  );
};

const BillView: React.FC<{ billId: string; canChange: boolean; onClose: () => void }> = ({ billId, canChange, onClose }) => {
  const { data: bill, isLoading, isError, error } = useVendorBill(billId);
  const setItc = useSetItcStatus();
  const cancelBill = useCancelVendorBill();
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');

  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !bill) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The bill could not be loaded.')}</p>;
  const intra = bill.supplyType === 'intra_state';

  return (
    <div className="space-y-5">
      <DetailCard title="Vendor bill" columns={4}>
        <DetailItem label="Vendor">{bill.vendorName || '—'}</DetailItem>
        <DetailItem label="Vendor invoice">{bill.vendorInvoiceNumber}</DetailItem>
        <DetailItem label="Invoice date">{formatDate(bill.billDate)}</DetailItem>
        <DetailItem label="Due">{formatDate(bill.dueDate)}</DetailItem>
        <DetailItem label="Status"><Badge className={BILL_STATUS[bill.status]?.className}>{BILL_STATUS[bill.status]?.label}</Badge></DetailItem>
        <DetailItem label="Match"><Badge className={MATCH[bill.matchStatus].className} title={MATCH[bill.matchStatus].hint}>{MATCH[bill.matchStatus].label}</Badge></DetailItem>
        <DetailItem label="Purchase order">{bill.purchaseOrderNumber || '—'}</DetailItem>
        <DetailItem label="Vendor GSTIN">{bill.vendorGSTIN || '—'}</DetailItem>
        <DetailItem label="Taxable value">{formatMoney(bill.taxableAmount)}</DetailItem>
        <DetailItem label={intra ? 'CGST + SGST' : 'IGST'}>
          {intra ? `${formatMoney(bill.cgstAmount ?? 0)} + ${formatMoney(bill.sgstAmount ?? 0)}` : formatMoney(bill.igstAmount ?? 0)}
        </DetailItem>
        <DetailItem label="Other charges">{formatMoney(bill.otherCharges ?? 0)}</DetailItem>
        <DetailItem label="Bill total">{formatMoney(bill.grandTotal)}</DetailItem>
        <DetailItem label="Paid">{formatMoney(bill.paidAmount)}</DetailItem>
        <DetailItem label="Still owed"><span className={bill.balance > 0 ? 'text-red-600' : undefined}>{formatMoney(bill.balance)}</span></DetailItem>
        <DetailItem label="Input tax credit" className="col-span-2">
          {canChange && bill.status !== 'cancelled' ? (
            <select
              value={bill.itcStatus}
              disabled={setItc.isPending}
              onChange={(event) =>
                setItc.mutate(
                  { id: bill.id, itcStatus: event.target.value as ItcStatus },
                  { onSuccess: () => toast.success('Input tax credit decision saved'), onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The decision could not be saved')) },
                )
              }
              aria-label="Input tax credit"
              className={cn(inputClass, 'font-normal')}
            >
              {(Object.keys(ITC) as ItcStatus[]).map((key) => (
                <option key={key} value={key}>{ITC[key]}</option>
              ))}
            </select>
          ) : (
            ITC[bill.itcStatus]
          )}
        </DetailItem>
        {bill.status === 'cancelled' && (
          <DetailItem label="Cancelled because" className="col-span-2 lg:col-span-4"><span className="font-normal text-red-700">{bill.cancelReason || '—'}</span></DetailItem>
        )}
      </DetailCard>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className={thClass}>Line</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Quantity</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Billed at</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Ordered at</th>
              <th scope="col" className={cn(thClass, 'text-right')}>GST</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Taxable</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(bill.items ?? []).map((item) => {
              const differs = item.orderedPrice !== null && Math.abs(item.orderedPrice - item.unitPrice) >= 0.005;
              return (
                <tr key={item.id}>
                  <td className="px-4 py-2.5">
                    <p className="text-sm font-medium text-gray-900">{item.description}</p>
                    <p className="text-xs text-gray-500">{[item.grnNumber ?? 'Not against a receipt', item.hsnCode ? `HSN ${item.hsnCode}` : null].filter(Boolean).join(' · ')}</p>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-900 tabular-nums">{formatNumber(item.quantity)} {item.unit ?? ''}</td>
                  <td className={cn('whitespace-nowrap px-4 py-2.5 text-right text-sm tabular-nums', differs ? 'font-medium text-amber-700' : 'text-gray-900')}>{formatMoney(item.unitPrice)}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-600 tabular-nums">{item.orderedPrice === null ? '—' : formatMoney(item.orderedPrice)}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-700 tabular-nums">{formatNumber(item.taxRate, 2)}%</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm font-medium text-gray-900 tabular-nums">{formatMoney(item.taxableAmount)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(bill.payments ?? []).length > 0 && (
        <p className="text-sm text-gray-600">
          Paid by {(bill.payments ?? []).map((payment) => `${payment.paymentNumber} (${formatMoney(payment.amount)}, ${formatDate(payment.paymentDate)})`).join(', ')}.
        </p>
      )}

      {cancelling && (
        <div className="space-y-3 rounded-lg bg-red-50 p-4 ring-1 ring-red-600/20">
          <p className="text-sm text-red-800">The goods this bill covers can be billed again.</p>
          <Field label="Reason" htmlFor="vb-cancel-reason" required>
            <input id="vb-cancel-reason" type="text" maxLength={300} autoFocus value={reason} onChange={(event) => setReason(event.target.value)} className={inputClass} />
          </Field>
        </div>
      )}

      <FormActions>
        {cancelling ? (
          <>
            <button type="button" onClick={() => setCancelling(false)} disabled={cancelBill.isPending} className={secondaryButtonClass}>Back</button>
            <button
              type="button"
              disabled={cancelBill.isPending || reason.trim().length < 3}
              onClick={() =>
                cancelBill.mutate(
                  { id: bill.id, reason: reason.trim() },
                  { onSuccess: () => { toast.success(`${bill.billNumber} cancelled`); setCancelling(false); }, onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The bill could not be cancelled')) },
                )
              }
              className={dangerButtonClass}
            >
              {cancelBill.isPending ? 'Cancelling…' : 'Cancel Bill'}
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={onClose} className={secondaryButtonClass}>Close</button>
            {bill.status === 'posted' && bill.paidAmount === 0 && canChange && (
              <button type="button" onClick={() => setCancelling(true)} className={secondaryButtonClass}>Cancel Bill</button>
            )}
          </>
        )}
      </FormActions>
    </div>
  );
};

// ---------------------------------------------------------------- vendor payments

// The bills still owed to one vendor, each with an amount box
const BillAllocation: React.FC<{ vendorId: string; available: number; amounts: Record<string, string>; onChange: (amounts: Record<string, string>) => void }> = ({ vendorId, available, amounts, onChange }) => {
  const { data, isLoading } = useVendorBills({ vendorId, unpaid: true, limit: 200 }, !!vendorId);
  const bills = [...(data?.data ?? [])].sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  const allocated = round2(Object.values(amounts).reduce((sum, value) => sum + (Number(value) || 0), 0));
  const oldestFirst = () => {
    let left = available;
    const next: Record<string, string> = {};
    bills.forEach((bill) => {
      const take = round2(Math.min(left, bill.balance));
      if (take > 0) next[bill.id] = String(take);
      left = round2(left - take);
    });
    onChange(next);
  };
  if (!vendorId) return null;
  return (
    <section className="rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-col gap-2 rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Set against bills</h4>
          <p className="mt-0.5 text-xs text-gray-500">What is not set against a bill stays as an advance with the vendor.</p>
        </div>
        {bills.length > 0 && available > 0 && (
          <button type="button" onClick={oldestFirst} className="self-start rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50 sm:self-auto">Oldest first</button>
        )}
      </div>
      {isLoading ? (
        <p className="px-4 py-6 text-sm text-gray-500">Loading…</p>
      ) : bills.length === 0 ? (
        <p className="px-4 py-6 text-sm text-gray-500">No bill of this vendor is waiting for payment. The whole amount will be an advance.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th scope="col" className={thClass}>Bill</th>
                <th scope="col" className={thClass}>Due</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Owed</th>
                <th scope="col" className={cn(thClass, 'w-44 text-right')}>Pay now</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {bills.map((bill) => (
                <tr key={bill.id}>
                  <td className="px-4 py-2.5">
                    <p className="text-sm font-medium text-gray-900">{bill.vendorInvoiceNumber}</p>
                    <p className="text-xs text-gray-500">{bill.billNumber} · {formatDate(bill.billDate)}</p>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-sm">
                    <span className={(bill.daysOverdue ?? 0) > 0 ? 'font-medium text-red-600' : 'text-gray-700'}>{formatDate(bill.dueDate)}</span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm font-medium text-gray-900 tabular-nums">{formatMoney(bill.balance)}</td>
                  <td className="px-4 py-2.5">
                    <input type="number" min="0" step="0.01" max={bill.balance} value={amounts[bill.id] ?? ''} onChange={(event) => onChange({ ...amounts, [bill.id]: event.target.value })} aria-label={`Amount for ${bill.vendorInvoiceNumber}`} className={cn(inputClass, 'text-right tabular-nums', Number(amounts[bill.id]) > bill.balance && inputErrorClass)} placeholder="0.00" />
                    {Number(amounts[bill.id]) > bill.balance && <p className="mt-1 text-right text-xs text-red-600">More than is owed</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-b-lg border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm">
        <span className="text-gray-600">Set against bills <span className="font-semibold text-gray-900 tabular-nums">{formatMoney(allocated)}</span></span>
        <span className={cn('font-semibold tabular-nums', allocated > available ? 'text-red-600' : 'text-gray-900')}>
          {allocated > available ? `${formatMoney(round2(allocated - available))} more than is available` : `${formatMoney(round2(available - allocated))} stays as an advance`}
        </span>
      </div>
    </section>
  );
};

const toAllocations = (amounts: Record<string, string>) =>
  Object.entries(amounts)
    .map(([vendorBillId, value]) => ({ vendorBillId, amount: round2(Number(value) || 0) }))
    .filter((allocation) => allocation.amount > 0);

const PaymentForm: React.FC<{ isPending: boolean; onCancel: () => void; onSubmit: (data: VendorPaymentInput) => void }> = ({ isPending, onCancel, onSubmit }) => {
  const { data: vendors = [] } = useActiveVendors();
  const [form, setForm] = useState({ vendorId: '', paymentDate: today(), amount: '', mode: 'bank_transfer', referenceNumber: '', bankName: '', notes: '' });
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const amount = Number(form.amount) || 0;
  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => {
      const { [field]: _cleared, ...rest } = prev;
      return rest;
    });
  };
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const allocations = toAllocations(amounts);
    const allocated = round2(allocations.reduce((sum, allocation) => sum + allocation.amount, 0));
    const next: Record<string, string> = {};
    if (!form.vendorId) next.vendorId = 'Choose the vendor being paid';
    if (!(amount > 0)) next.amount = 'Enter the amount paid';
    else if (round2(amount) !== amount) next.amount = 'Use at most two decimals';
    if (!form.paymentDate) next.paymentDate = 'Enter the date';
    else if (form.paymentDate > today()) next.paymentDate = 'Not in the future';
    if (!next.amount && allocated > amount) next.allocations = 'More is set against bills than was paid';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({ vendorId: form.vendorId, paymentDate: form.paymentDate, amount, mode: form.mode, referenceNumber: form.referenceNumber.trim() || null, bankName: form.bankName.trim() || null, notes: form.notes.trim() || null, allocations });
  };
  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <FormCard title="Payment" description="Who is paid, how much and how">
        <Field label="Vendor" htmlFor="vp-vendor" required error={errors.vendorId} className="sm:col-span-2">
          <select id="vp-vendor" value={form.vendorId} onChange={(event) => { set('vendorId')(event); setAmounts({}); }} className={cn(inputClass, errors.vendorId && inputErrorClass)}>
            <option value="">Select…</option>
            {vendors.map((vendor) => (
              <option key={vendor.id} value={vendor.id}>{vendor.name}{vendor.code ? ` (${vendor.code})` : ''}</option>
            ))}
          </select>
        </Field>
        <Field label="Amount paid (₹)" htmlFor="vp-amount" required error={errors.amount}>
          <input id="vp-amount" type="number" min="0" step="0.01" value={form.amount} onChange={set('amount')} className={cn(inputClass, 'text-right tabular-nums', errors.amount && inputErrorClass)} placeholder="0.00" />
        </Field>
        <Field label="Date paid" htmlFor="vp-date" required error={errors.paymentDate}>
          <input id="vp-date" type="date" max={today()} value={form.paymentDate} onChange={set('paymentDate')} className={cn(inputClass, errors.paymentDate && inputErrorClass)} />
        </Field>
        <Field label="Paid by" htmlFor="vp-mode">
          <select id="vp-mode" value={form.mode} onChange={set('mode')} className={inputClass}>
            {MODES.map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
          </select>
        </Field>
        <Field label="Reference" htmlFor="vp-reference" hint="Cheque number, UTR or UPI reference">
          <input id="vp-reference" type="text" maxLength={60} value={form.referenceNumber} onChange={set('referenceNumber')} className={inputClass} />
        </Field>
        <Field label="Bank" htmlFor="vp-bank">
          <input id="vp-bank" type="text" maxLength={100} value={form.bankName} onChange={set('bankName')} className={inputClass} />
        </Field>
        <Field label="Notes" htmlFor="vp-notes">
          <input id="vp-notes" type="text" maxLength={500} value={form.notes} onChange={set('notes')} className={inputClass} />
        </Field>
      </FormCard>
      <BillAllocation vendorId={form.vendorId} available={amount} amounts={amounts} onChange={setAmounts} />
      {errors.allocations && <p className="text-sm text-red-600">{errors.allocations}</p>}
      <FormActions>
        <button type="button" onClick={onCancel} disabled={isPending} className={secondaryButtonClass}>Cancel</button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>{isPending ? 'Saving…' : 'Save Payment'}</button>
      </FormActions>
    </form>
  );
};

const PaymentView: React.FC<{ paymentId: string; canChange: boolean; onClose: () => void }> = ({ paymentId, canChange, onClose }) => {
  const { data: payment, isLoading, isError, error } = useVendorPayment(paymentId);
  const allocatePayment = useAllocateVendorPayment();
  const cancelPayment = useCancelVendorPayment();
  const [mode, setMode] = useState<'view' | 'allocate' | 'cancel'>('view');
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');

  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !payment) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The payment could not be loaded.')}</p>;
  const allocations = toAllocations(amounts);
  const wanted = round2(allocations.reduce((sum, allocation) => sum + allocation.amount, 0));
  const busy = allocatePayment.isPending || cancelPayment.isPending;

  return (
    <div className="space-y-5">
      <DetailCard title="Payment" columns={4}>
        <DetailItem label="Vendor">{payment.vendorName || '—'}</DetailItem>
        <DetailItem label="Date paid">{formatDate(payment.paymentDate)}</DetailItem>
        <DetailItem label="Paid by">{modeLabel(payment.mode)}</DetailItem>
        <DetailItem label="Status"><Badge className={payment.status === 'posted' ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-red-50 text-red-700 ring-red-600/20'}>{payment.status === 'posted' ? 'Paid' : 'Cancelled'}</Badge></DetailItem>
        <DetailItem label="Amount">{formatMoney(payment.amount)}</DetailItem>
        <DetailItem label="Set against bills">{formatMoney(payment.allocatedAmount)}</DetailItem>
        <DetailItem label="Advance"><span className={payment.unallocatedAmount > 0 ? 'text-amber-700' : undefined}>{formatMoney(payment.unallocatedAmount)}</span></DetailItem>
        <DetailItem label="Reference">{[payment.referenceNumber, payment.bankName].filter(Boolean).join(' · ') || '—'}</DetailItem>
        {payment.status === 'cancelled' && (
          <DetailItem label="Cancelled because" className="col-span-2 lg:col-span-4"><span className="font-normal text-red-700">{payment.cancelReason || '—'}</span></DetailItem>
        )}
      </DetailCard>

      {mode !== 'allocate' && (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>Bill paid</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Bill total</th>
                <th scope="col" className={cn(thClass, 'text-right')}>From this payment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(payment.allocations ?? []).length === 0 ? (
                <tr><td colSpan={3} className="px-4 py-5 text-center text-sm text-gray-500">{payment.status === 'cancelled' ? 'This payment pays no bill.' : 'Nothing is set against a bill yet; the whole amount is an advance.'}</td></tr>
              ) : (
                (payment.allocations ?? []).map((allocation) => (
                  <tr key={allocation.id}>
                    <td className="px-4 py-2.5">
                      <p className="text-sm font-medium text-gray-900">{allocation.vendorInvoiceNumber}</p>
                      <p className="text-xs text-gray-500">{allocation.billNumber} · {formatDate(allocation.billDate)}</p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-700 tabular-nums">{formatMoney(allocation.grandTotal)}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm font-medium text-gray-900 tabular-nums">{formatMoney(allocation.amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {mode === 'allocate' && <BillAllocation vendorId={payment.vendorId} available={payment.unallocatedAmount} amounts={amounts} onChange={setAmounts} />}
      {mode === 'cancel' && (
        <div className="space-y-3 rounded-lg bg-red-50 p-4 ring-1 ring-red-600/20">
          <p className="text-sm text-red-800">The bills this payment paid are owed again by what it paid.</p>
          <Field label="Reason" htmlFor="vp-cancel-reason" required>
            <input id="vp-cancel-reason" type="text" maxLength={300} autoFocus value={reason} onChange={(event) => setReason(event.target.value)} className={inputClass} />
          </Field>
        </div>
      )}

      <FormActions>
        {mode === 'view' ? (
          <>
            <button type="button" onClick={onClose} className={secondaryButtonClass}>Close</button>
            {payment.status === 'posted' && canChange && <button type="button" onClick={() => setMode('cancel')} className={secondaryButtonClass}>Cancel Payment</button>}
            {payment.status === 'posted' && canChange && payment.unallocatedAmount > 0 && <button type="button" onClick={() => setMode('allocate')} className={primaryButtonClass}>Set Against Bills</button>}
          </>
        ) : (
          <>
            <button type="button" onClick={() => setMode('view')} disabled={busy} className={secondaryButtonClass}>Back</button>
            {mode === 'allocate' ? (
              <button
                type="button"
                disabled={busy || wanted <= 0 || wanted > payment.unallocatedAmount}
                onClick={() => allocatePayment.mutate({ id: payment.id, allocations }, { onSuccess: () => { toast.success(`${formatMoney(wanted)} set against bills`); setAmounts({}); setMode('view'); }, onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The amount could not be set against the bills')) })}
                className={primaryButtonClass}
              >
                {allocatePayment.isPending ? 'Saving…' : 'Save'}
              </button>
            ) : (
              <button
                type="button"
                disabled={busy || reason.trim().length < 3}
                onClick={() => cancelPayment.mutate({ id: payment.id, reason: reason.trim() }, { onSuccess: () => { toast.success(`${payment.paymentNumber} cancelled`); setMode('view'); }, onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The payment could not be cancelled')) })}
                className={dangerButtonClass}
              >
                {cancelPayment.isPending ? 'Cancelling…' : 'Cancel Payment'}
              </button>
            )}
          </>
        )}
      </FormActions>
    </div>
  );
};

// ---------------------------------------------------------------- page

type Tab = 'bills' | 'payments' | 'ageing';
const AGEING_COLUMNS = [
  { key: 'notDue', label: 'Not due' },
  { key: 'days1to30', label: '1–30 days' },
  { key: 'days31to60', label: '31–60 days' },
  { key: 'days61to90', label: '61–90 days' },
  { key: 'over90', label: 'Over 90 days' },
  { key: 'total', label: 'Total owed' },
  { key: 'advances', label: 'Advances' },
] as const;

const Payables: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('purchase.create');
  const canChange = can('purchase.update');

  const [tab, setTab] = useState<Tab>('bills');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [billFilter, setBillFilter] = useState<'' | 'unpaid' | 'overdue' | 'review'>('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage);
  const [billFormOpen, setBillFormOpen] = useState(false);
  const [paymentFormOpen, setPaymentFormOpen] = useState(false);
  const [viewingBillId, setViewingBillId] = useState<string | null>(null);
  const [viewingPaymentId, setViewingPaymentId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const bills = useVendorBills({ page, limit, search: search || undefined, unpaid: billFilter === 'unpaid', overdueOnly: billFilter === 'overdue', matchStatus: billFilter === 'review' ? 'price_variance' : '' }, tab === 'bills');
  const payments = useVendorPayments({ page, limit, search: search || undefined }, tab === 'payments');
  const ageing = usePayablesAgeing(tab === 'ageing');
  const { data: billSummary } = useVendorBillSummary();
  const { data: paymentSummary } = useVendorPaymentSummary();
  const createBill = useCreateVendorBill();
  const createPayment = useCreateVendorPayment();

  const active = tab === 'bills' ? bills : payments;
  const total = tab === 'ageing' ? 0 : active.data?.total ?? 0;
  const totalPages = tab === 'ageing' ? 1 : active.data?.totalPages ?? 1;
  useEffect(() => {
    if (tab !== 'ageing' && active.data && page > active.data.totalPages) setPage(active.data.totalPages);
  }, [tab, active.data, page]);

  const changeTab = (next: Tab) => {
    setTab(next);
    setPage(1);
    setSearchInput('');
    setSearch('');
  };

  const stats = [
    { label: 'Owed to vendors', value: billSummary ? formatMoney(billSummary.payable) : '—', hint: `${billSummary ? formatNumber(billSummary.openBills) : '—'} bill(s) not fully paid` },
    { label: 'Overdue', value: billSummary ? formatMoney(billSummary.overdue) : '—', hint: `${billSummary ? formatNumber(billSummary.overdueBills) : '—'} bill(s) past the due date` },
    { label: 'Received, not billed', value: billSummary ? formatMoney(billSummary.unbilledReceiptValue) : '—', hint: `${billSummary ? formatNumber(billSummary.unbilledReceiptLines) : '—'} goods receipt line(s) awaiting a bill` },
    { label: 'Paid this month', value: paymentSummary ? formatMoney(paymentSummary.paidThisMonth) : '—', hint: `${paymentSummary ? formatMoney(paymentSummary.advances) : '—'} held as advances` },
  ];
  const tabs: { key: Tab; label: string }[] = [
    { key: 'bills', label: 'Vendor bills' },
    { key: 'payments', label: 'Payments made' },
    { key: 'ageing', label: 'Ageing by vendor' },
  ];
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Vendor Bills &amp; Payments</h1>
          <p className="mt-1 text-sm text-gray-500">Vendor invoices matched to what was ordered and received, the payments made, and what is still owed.</p>
        </div>
        {canCreate && (
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={() => setPaymentFormOpen(true)} className={secondaryButtonClass}>
              Pay Vendor
            </button>
            <button type="button" onClick={() => setBillFormOpen(true)} className={primaryButtonClass}>
              <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
              Enter Vendor Bill
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 truncate text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex gap-1 border-b border-gray-200 px-4 pt-3" role="tablist">
          {tabs.map((item) => (
            <button key={item.key} type="button" role="tab" aria-selected={tab === item.key} onClick={() => changeTab(item.key)} className={cn('-mb-px border-b-2 px-3 py-2 text-sm font-medium', tab === item.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
              {item.label}
            </button>
          ))}
        </div>

        {tab !== 'ageing' && (
          <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder={tab === 'bills' ? 'Search by vendor, vendor invoice, bill or order number' : 'Search by payment number, vendor or reference'} aria-label="Search" className={cn(inputClass, 'pl-9')} />
            </div>
            {tab === 'bills' && (
              <select value={billFilter} onChange={(event) => { setBillFilter(event.target.value as typeof billFilter); setPage(1); }} aria-label="Show" className={cn(inputClass, 'lg:w-56')}>
                <option value="">All bills</option>
                <option value="unpaid">Still owed</option>
                <option value="overdue">Overdue</option>
                <option value="review">Price differs from the order</option>
              </select>
            )}
          </div>
        )}

        {tab === 'bills' && (
          <div className={cn('overflow-x-auto', bills.isFetching && bills.data && 'opacity-60')}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Vendor invoice</th>
                  <th scope="col" className={thClass}>Vendor</th>
                  <th scope="col" className={thClass}>Due</th>
                  <th scope="col" className={thClass}>Match</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Total</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Still owed</th>
                  <th scope="col" className={thClass}>Status</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {bills.isLoading ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
                ) : (bills.data?.data ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center">
                      <p className="text-sm font-medium text-gray-900">{search || billFilter ? 'No bill matches' : 'No vendor bills yet'}</p>
                      <p className="mt-1 text-sm text-gray-500">Enter a vendor's invoice against the goods received from them.</p>
                    </td>
                  </tr>
                ) : (
                  (bills.data?.data ?? []).map((bill) => (
                    <tr key={bill.id} className={cn('hover:bg-gray-50', bill.status === 'cancelled' && 'bg-gray-50/60')}>
                      <td className="whitespace-nowrap px-4 py-3">
                        <button type="button" onClick={() => setViewingBillId(bill.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{bill.vendorInvoiceNumber}</button>
                        <p className="mt-0.5 text-xs text-gray-500">{bill.billNumber} · {formatDate(bill.billDate)}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{bill.vendorName || '—'}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <span className={(bill.daysOverdue ?? 0) > 0 ? 'font-medium text-red-600' : 'text-gray-700'}>{formatDate(bill.dueDate)}</span>
                        {(bill.daysOverdue ?? 0) > 0 && <span className="block text-xs text-red-600">{bill.daysOverdue} day(s) overdue</span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3"><Badge className={MATCH[bill.matchStatus].className} title={MATCH[bill.matchStatus].hint}>{MATCH[bill.matchStatus].label}</Badge></td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatMoney(bill.grandTotal)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-gray-900 tabular-nums">{bill.balance > 0 ? formatMoney(bill.balance) : '—'}</td>
                      <td className="whitespace-nowrap px-4 py-3"><Badge className={BILL_STATUS[bill.status]?.className}>{BILL_STATUS[bill.status]?.label}</Badge></td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <button type="button" onClick={() => setViewingBillId(bill.id)} title="View" aria-label={`View ${bill.billNumber}`} className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"><EyeIcon className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'payments' && (
          <div className={cn('overflow-x-auto', payments.isFetching && payments.data && 'opacity-60')}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Payment</th>
                  <th scope="col" className={thClass}>Vendor</th>
                  <th scope="col" className={thClass}>Paid by</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Amount</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Advance</th>
                  <th scope="col" className={thClass}>Status</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payments.isLoading ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
                ) : (payments.data?.data ?? []).length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-500">{search ? 'No payment matches.' : 'No payments to vendors yet.'}</td></tr>
                ) : (
                  (payments.data?.data ?? []).map((payment) => (
                    <tr key={payment.id} className={cn('hover:bg-gray-50', payment.status === 'cancelled' && 'bg-gray-50/60')}>
                      <td className="whitespace-nowrap px-4 py-3">
                        <button type="button" onClick={() => setViewingPaymentId(payment.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{payment.paymentNumber}</button>
                        <p className="mt-0.5 text-xs text-gray-500">{formatDate(payment.paymentDate)}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{payment.vendorName || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{modeLabel(payment.mode)}{payment.referenceNumber && <span className="block text-xs text-gray-500">{payment.referenceNumber}</span>}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">{formatMoney(payment.amount)}</td>
                      <td className={cn('whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums', payment.unallocatedAmount > 0 && payment.status === 'posted' ? 'font-medium text-amber-700' : 'text-gray-400')}>{payment.unallocatedAmount > 0 && payment.status === 'posted' ? formatMoney(payment.unallocatedAmount) : '—'}</td>
                      <td className="whitespace-nowrap px-4 py-3"><Badge className={payment.status === 'posted' ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-red-50 text-red-700 ring-red-600/20'}>{payment.status === 'posted' ? 'Paid' : 'Cancelled'}</Badge></td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <button type="button" onClick={() => setViewingPaymentId(payment.id)} title="View" aria-label={`View ${payment.paymentNumber}`} className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"><EyeIcon className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'ageing' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Vendor</th>
                  {AGEING_COLUMNS.map((column) => (<th key={column.key} scope="col" className={cn(thClass, 'text-right')}>{column.label}</th>))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ageing.isLoading ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
                ) : (ageing.data?.data ?? []).length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-500">Nothing is owed to any vendor on an entered bill.</td></tr>
                ) : (
                  (ageing.data?.data ?? []).map((row) => (
                    <tr key={row.vendorId} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{row.vendorName || '—'}</p>
                        <p className="mt-0.5 text-xs text-gray-500">{row.bills} bill(s)</p>
                      </td>
                      {AGEING_COLUMNS.map((column) => (
                        <td key={column.key} className={cn('whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums', column.key === 'total' ? 'font-semibold text-gray-900' : row[column.key] > 0 && column.key !== 'notDue' && column.key !== 'advances' ? 'text-red-600' : 'text-gray-700')}>
                          {row[column.key] > 0 ? formatMoney(row[column.key]) : '—'}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
              {(ageing.data?.data ?? []).length > 0 && (
                <tfoot className="bg-gray-50">
                  <tr>
                    <td className="px-4 py-3 text-sm font-medium text-gray-700">Total</td>
                    {AGEING_COLUMNS.map((column) => (<td key={column.key} className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-gray-900 tabular-nums">{formatMoney(ageing.data?.totals[column.key] ?? 0)}</td>))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {tab !== 'ageing' && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
              <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
              <label className="flex items-center gap-2">
                Rows per page
                <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                  {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
                </select>
              </label>
            </div>
            {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
          </div>
        )}
      </div>

      <Modal isOpen={billFormOpen} onClose={() => !createBill.isPending && setBillFormOpen(false)} title="Enter Vendor Bill" description="The vendor's invoice, against the goods received from them." size="2xl">
        {billFormOpen && (
          <BillForm
            isPending={createBill.isPending}
            onCancel={() => setBillFormOpen(false)}
            onSubmit={(input) =>
              createBill.mutate(input, {
                onSuccess: (saved) => { toast.success(`${saved.billNumber} entered: ${formatMoney(saved.grandTotal)} owed to ${saved.vendorName}`); setBillFormOpen(false); setTab('bills'); setViewingBillId(saved.id); },
                onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The bill could not be saved')),
              })
            }
          />
        )}
      </Modal>
      <Modal isOpen={paymentFormOpen} onClose={() => !createPayment.isPending && setPaymentFormOpen(false)} title="Pay Vendor" description="Record money paid to a vendor and set it against the bills it pays." size="2xl">
        {paymentFormOpen && (
          <PaymentForm
            isPending={createPayment.isPending}
            onCancel={() => setPaymentFormOpen(false)}
            onSubmit={(input) =>
              createPayment.mutate(input, {
                onSuccess: (saved) => { toast.success(`${saved.paymentNumber}: ${formatMoney(saved.amount)} paid`); setPaymentFormOpen(false); setTab('payments'); setViewingPaymentId(saved.id); },
                onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The payment could not be saved')),
              })
            }
          />
        )}
      </Modal>
      <Modal isOpen={!!viewingBillId && !billFormOpen} onClose={() => setViewingBillId(null)} title="Vendor Bill" size="2xl">
        {viewingBillId && !billFormOpen && <BillView key={viewingBillId} billId={viewingBillId} canChange={canChange} onClose={() => setViewingBillId(null)} />}
      </Modal>
      <Modal isOpen={!!viewingPaymentId && !paymentFormOpen} onClose={() => setViewingPaymentId(null)} title="Vendor Payment" size="2xl">
        {viewingPaymentId && !paymentFormOpen && <PaymentView key={viewingPaymentId} paymentId={viewingPaymentId} canChange={canChange} onClose={() => setViewingPaymentId(null)} />}
      </Modal>
    </div>
  );
};

export default Payables;
